import React, { useState, useEffect, useRef } from "react";
import { Upload, UploadProps } from "antd";
import type { UploadFile, RcFile } from "antd/lib/upload/interface";
import { Container, Typography, IconButton, Select } from "components/ui";
import { Icon } from "components/hds";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { useToaster } from "contexts/Toaster";
import { FILENAME } from "common/regex";
import {
	imageAccept,
	objectAccept,
	materialAccept,
	materialAcceptRef,
	ext,
} from "common/filetypes";
import { ItemKinds } from "common/itemKinds";
import {
	MAX_UPLOAD_REQUEST_BYTES,
	MAX_UPLOAD_REQUEST_LABEL_BYTES,
} from "common/constants/upload";
import formatBytes from "utils/formatBytes";

const { Dragger } = Upload;

// The "Gesamt" row appears from this share of the upload limit on.
const TOTAL_VISIBLE_FROM_SHARE = 0.9;

/* const acceptedExtensions = [...imageAccept, ...objectAccept, ...materialAccept]
	.map((currExt: string) => `.${currExt}`)
	.join(); */

const acceptedExtensions: any = {
	[ItemKinds.Scene]: [
		...objectAccept,
		...materialAcceptRef,
		...materialAccept,
	]
		.map((currExt: string) => `.${currExt}`)
		.join(),
	[ItemKinds.Object]: [
		...objectAccept,
		...materialAcceptRef,
		...materialAccept,
	]
		.map((currExt: string) => `.${currExt}`)
		.join(),
	[ItemKinds.Material]: [...materialAcceptRef]
		.map((currExt: string) => `.${currExt}`)
		.join(),
};

interface HegiasDraggerProps {
	setFiles: (files: UploadFile[]) => void;
	setWarnings: (warnings: string[]) => void;
	setErrors: (errors: string[]) => void;
	materialsMap: { map: string; file: UploadFile }[];
	setMaterialsMap: React.Dispatch<React.SetStateAction<{ map: string; file: UploadFile }[]>>;
	kind: keyof typeof ItemKinds;
	maxSize?: number;
	/** Accept several 3D model files in one drop (they all go into one scene). */
	allowMultipleModels?: boolean;
}

const HegiasDragger = ({
	setFiles,
	setWarnings,
	setErrors,
	materialsMap,
	setMaterialsMap,
	kind,
	maxSize = 0,
	allowMultipleModels = false,
}: HegiasDraggerProps) => {
	const { t, i18n } = useTranslation();

	const [currentWarnings, setCurrentWarnings] = useState<string[]>([]);
	const [currentErrors, setCurrentErrors] = useState<string[]>([]);

	const [currentFileList, setCurrentFileList] = useState<UploadFile[]>([]);

	// The parent's list follows ours, rather than being set next to it from
	// beforeUpload/customRequest: antd calls those once per file of a drop, all
	// from the same render, so a list built from `currentFileList` there is stale
	// and a multi-file drop kept only its last file. Skips the mount, where the
	// parent may already hold files that didn't come through here.
	const isFirstFileListSync = useRef(true);
	useEffect(() => {
		if (isFirstFileListSync.current) {
			isFirstFileListSync.current = false;
			return;
		}
		setFiles(currentFileList);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentFileList]);

	const toaster = useToaster();

	const parseFileList = () => {
		let containsObj = false;
		let containsMtl = false;
		let containsGltf = false;
		let containsBin = false;
		let containsGlb = false;
		let containsUsd = false;
		let containsIfc = false;

		/* 	let containDiffuseMap = false; */

		const objectFiles: UploadFile[] = [];
		const textureFiles: UploadFile[] = [];
		const materialFiles: UploadFile[] = [];

		currentFileList.forEach((file) => {
			const extension = ext(file.name);
			if (extension === "obj") {
				containsObj = true;
			}
			if (extension === "mtl") {
				containsMtl = true;
			}
			if (extension === "gltf") {
				containsGltf = true;
			}
			if (extension === "bin") {
				containsBin = true;
			}
			if (extension === "glb") {
				containsGlb = true;
			}
			if (extension.includes("usd")) {
				containsUsd = true;
			}
			if (extension === "ifc") {
				containsIfc = true;
			}

			if (
				kind !== ItemKinds.Material &&
				objectAccept.includes(extension)
			) {
				objectFiles.push(file);
			}

			if (
				kind !== ItemKinds.Material &&
				imageAccept.includes(extension)
			) {
				textureFiles.push(file);
			}

			if (
				kind === ItemKinds.Material &&
				materialAcceptRef.includes(extension)
			) {
				materialFiles.push(file);
			}
			/* if (materialAccept.includes(extension)) {
				materialFiles.push(file);
			} */
		});

		return {
			containsObj,
			containsMtl,
			containsGltf,
			containsIfc,
			containsBin,
			containsGlb,
			containsUsd,
			objectFiles,
			textureFiles,
			materialFiles,
		};
	};

	// Everything but a material goes up in one request, and the server caps the
	// whole request body - so the limit is on the sum. Materials send every map
	// in its own request, where the per-file limit is the one that applies.
	const checksTotalSize = kind !== ItemKinds.Material;
	const totalBytes = currentFileList.reduce(
		(sum, file) => sum + (file.size ?? 0),
		0,
	);
	const totalTooLarge =
		checksTotalSize && totalBytes > MAX_UPLOAD_REQUEST_BYTES;
	// The total only matters near the limit; below that it just raises questions.
	const showTotal =
		checksTotalSize &&
		totalBytes > MAX_UPLOAD_REQUEST_LABEL_BYTES * TOTAL_VISIBLE_FROM_SHARE;

	const checkDuplicateGeometryFile = (
		file: UploadFile,
		fileList: UploadFile[],
	) => {
		const dup = [
			...fileList.filter((item) => item.name !== file.name),
			file,
		].filter((item) => objectAccept.includes(ext(item.name)));

		return dup.length > 1;
	};

	const beforeUpload: (file: RcFile, fileList: RcFile[]) => boolean = (
		file,
		fileList,
	) => {
		if (file.size > maxSize && maxSize !== 0) {
			toaster.error({
				message: t("app.global.msg.title.note"),
				description: t("app.AntdDragger.msg.fileTooBig"),
			});
			setCurrentFileList((currFileList) => [...currFileList]);
			return false;
		}

		if (
			!allowMultipleModels &&
			checkDuplicateGeometryFile(file as RcFile, fileList) &&
			objectAccept.includes(ext(file.name))
		) {
			toaster.error({
				message: t("app.global.msg.title.note"),
				description: t("app.AntdDragger.msg.doubleGeometry"),
			});
			setCurrentFileList((currFileList) => [...currFileList]);
			return false;
		}

		if (
			!allowMultipleModels &&
			checkDuplicateGeometryFile(file, currentFileList) &&
			objectAccept.includes(ext(file.name))
		) {
			toaster.error({
				message: t("app.global.msg.title.note"),
				description: t("app.AntdDragger.msg.geometryAlreadyUploaded"),
			});
			setCurrentFileList((currFileList) => [...currFileList]);

			return false;
		}

		// Two files of the same name can't both be uploaded: the server stores
		// them side by side under their names. Earlier in this drop, or already
		// in the list from a previous one.
		const firstInDrop = fileList.find((item) => item.name === file.name);
		if (
			(firstInDrop && firstInDrop.uid !== file.uid) ||
			currentFileList.some((item) => item.name === file.name)
		) {
			toaster.error({
				message: t("app.global.msg.title.note"),
				description: t("app.AntdDragger.msg.duplicateFileName"),
			});
			setCurrentFileList((currFileList) => [...currFileList]);

			return false;
		}

		if (!FILENAME.test(file.name)) {
			toaster.error({
				message: t("app.global.msg.title.note"),
				description: t("app.AntdDragger.msg.invalidFilename"),
			});
			setCurrentFileList((currFileList) => [...currFileList]);

			return false;
		}

		// Create UploadFile structure with xhr property for form submission
		const uploadFile: UploadFile = {
			uid: file.uid,
			name: file.name,
			size: file.size,
			type: file.type,
			xhr: file as any, // Store actual file for form submission
			status: 'uploading',
			percent: 0,
		};

		setCurrentFileList((currFileList) => [...currFileList, uploadFile]);
		return true;
	};

	const customRequest: UploadProps['customRequest'] = (options) => {
		const { onSuccess, file } = options;
		return setTimeout(() => {
			// Cast file to RcFile for type safety
			const rcFile = file as RcFile;
			
			// Create UploadFile structure with xhr property for form submission
			const uploadFile: UploadFile = {
				uid: rcFile.uid,
				name: rcFile.name,
				size: rcFile.size,
				type: rcFile.type,
				xhr: rcFile as any, // Store actual file for form submission
				status: 'done',
				percent: 100,
			};
			replaceItem(uploadFile);
			if (onSuccess) onSuccess("ok");
		}, 50);
	};

	const checkWarnings = ({
		containsObj,
		containsMtl,
		containsGltf,
		containsBin,
		containsGlb,
		containsUsd,
		containsIfc,
		objectFiles,
		textureFiles,
		materialFiles,
	}: {
		containsObj: boolean;
		containsMtl: boolean;
		containsGltf: boolean;
		containsBin: boolean;
		containsGlb: boolean;
		containsUsd: boolean;
		containsIfc: boolean;
		objectFiles: UploadFile[];
		textureFiles: UploadFile[];
		materialFiles: UploadFile[];
	}) => {
		const listOfWarnings = [];

		if (containsObj && !containsMtl) {
			listOfWarnings.push("app.AntdDragger.warning.noMaterial");
		}
		if (containsGltf && !containsBin) {
			listOfWarnings.push("app.AntdDragger.warning.noBin");
		}
		if (
			!containsGlb &&
			!containsUsd &&
			objectFiles.length &&
			!textureFiles.length &&
			!containsIfc
		) {
			listOfWarnings.push("app.AntdDragger.warning.noTextures");
		}

		// Warn (but don't block) when some material files haven't been assigned a map slot yet.
		if (
			kind === ItemKinds.Material &&
			materialFiles.length !== 0 &&
			materialsMap.length !== materialFiles.length
		) {
			listOfWarnings.push("app.AntdDragger.error.missingMap");
		}

		return listOfWarnings;
	};

	const checkErrors = ({
		containsObj,
		containsMtl,
		containsGltf,
		containsBin,
		// containsGlb,
		// containsIfc,
		objectFiles,
		textureFiles,
	}: // materialFiles,
	{
		containsObj: boolean;
		containsMtl: boolean;
		containsGltf: boolean;
		containsBin: boolean;
		containsGlb: boolean;
		containsIfc: boolean;
		objectFiles: UploadFile[];
		textureFiles: UploadFile[];
		materialFiles: UploadFile[];
	}) => {
		const listOfErrors = [];

		if (containsBin && !containsGltf) {
			listOfErrors.push("app.AntdDragger.error.noGltf");
		}
		if (containsMtl && !containsObj) {
			listOfErrors.push("app.AntdDragger.error.noObj");
		}
		if (textureFiles.length && !objectFiles.length) {
			listOfErrors.push("app.AntdDragger.error.noModel");
		}
		if (totalTooLarge) {
			listOfErrors.push("app.AntdDragger.error.totalTooLarge");
		}

		// Check if diffuse map is set.
		if (kind === ItemKinds.Material) {
			const diffuseMapExist = materialsMap.some(
				(item: { map: string; file: UploadFile }) =>
					item.map === "diffuse",
			);

			if (!diffuseMapExist) {
				listOfErrors.push("app.AntdDragger.error.diffuseRequired");
			}
		}

		// Check if a map is already been used.
		if (kind === ItemKinds.Material && materialsMap.length !== 0) {
			const valuesToCheck = materialsMap.map((item) => item.map);

			const materialsMapDuplicated = valuesToCheck.filter(
				(item: any, index: any) => {
					return valuesToCheck.indexOf(item) !== index;
				},
			);

			if (materialsMapDuplicated.length !== 0) {
				listOfErrors.push("app.AntdDragger.error.mapUnique");
			}
		}

		setErrors(listOfErrors);

		return listOfErrors;
	};

	// By uid: `file` is a fresh object, so indexOf found nothing and the
	// splice(-1) overwrote whichever file happened to be last.
	const replaceItem = (file: UploadFile) => {
		setCurrentFileList((currFileList) =>
			currFileList.map((item) => (item.uid === file.uid ? file : item)),
		);
	};

	const onRemove = (file: UploadFile) => {
		setCurrentFileList((currFileList) =>
			currFileList.filter((item) => item.uid !== file.uid),
		);

		if (kind === ItemKinds.Material) {
			setMaterialsMap((prev) =>
				prev.filter((item: { map: string; file: UploadFile }) => item.file.uid !== file.uid)
			);
		}
	};

	const getName: (filename: string) => string = (filename) => {
		return filename.split(".").slice(0, -1).join(".");
	};

	const materialMapOptions: Array<{
		label: string;
		value:
			| "thumbnail"
			| "diffuse"
			| "normal"
			| "ambientOcclusion"
			| "mapARM"
			| "emissive"
			| "roughness"
			| "transparency"
			| "bump"
			| "metalness";
	}> = React.useMemo(
		() => [
			{
				label: t("app.global.general.thumbnail"),
				value: "thumbnail",
			},
			{
				label: t("app.createMaterial.modal.mapDiffuse"),
				value: "diffuse",
			},
			{
				label: t("app.createMaterial.modal.mapNormal"),
				value: "normal",
			},
			{
				label: t("app.createMaterial.modal.mapMetalness"),
				value: "metalness",
			},
			{
				label: t("app.createMaterial.modal.mapEmissive"),
				value: "emissive",
			},
			{
				label: t("app.createMaterial.modal.mapRoughness"),
				value: "roughness",
			},
			{
				label: t("app.createMaterial.modal.mapTransparency"),
				value: "transparency",
			},
			{
				label: t("app.createMaterial.modal.mapAO"),
				value: "ambientOcclusion",
			},
			{
				label: t("app.createMaterial.modal.mapBump"),
				value: "bump",
			},
			/* {
				label: t("app.createMaterial.modal.mapARM"),
				value: "mapARM",
			}, */
		],
		[t],
	);

	const getIntensityForMap = (map: string) => {
		switch (map) {
			/* case "displacement": */
			case "bump":
			case "emissive":
			case "AO":
				return "1";
			case "metalness":
			case "roughness":
				return "1.0";
			case "transparency":
				return "0.99";
			default:
				return "1";
		}
	};

	const renderLine: (file: UploadFile) => React.ReactChild = (
		file: UploadFile,
	) => {
		return (
			<StyledItemRow key={file.uid} kind={kind}>
				<Typography as="span">{getName(file.name)}</Typography>
				{kind === ItemKinds.Material && (
					<Select
						{...(({
							options: materialMapOptions,
							value: materialsMap.find((item) => item.file.uid === file.uid)?.map ?? "",
							onChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
								const map = e.target.value;
								setMaterialsMap((prev) => {
									const mapExist = prev.some(
										(item) => item.file.uid === file.uid,
									);
									if (!mapExist) {
										return [
											...prev,
											{ map, file, intensity: getIntensityForMap(map) },
										];
									}
									return prev.map((item) =>
										item.file.uid === file.uid
											? { map, file, intensity: getIntensityForMap(map) }
											: item,
									);
								});
							},
						}) as any)}
					/>
				)}
				<StyledSize>{formatBytes(file.size ?? 0, i18n.language)}</StyledSize>
				<Typography as="span">
					{ext(file.name).toUpperCase()}
				</Typography>
				<StyledLineEnd>
					{file.status === "done" ? (
						<Icon name="CorrectIcon" size="16px" color="primary" />
					) : (
						<Icon
							name="WarningIcon"
							size="16px"
							color="red"
							hoverColor="red"
						/>
					)}
					<IconButton size="mini" onClick={() => onRemove(file)}>
						<Icon
							name="DeleteIcon"
							size="50%"
							color="red"
							hoverColor="red"
						/>
					</IconButton>
				</StyledLineEnd>
			</StyledItemRow>
		);
	};

	const parsedFileList = parseFileList();

	const { objectFiles, textureFiles, materialFiles } = parsedFileList;

	useEffect(() => {
		const updatedParsedFileList = parseFileList();

		const listOfWarnings = checkWarnings(updatedParsedFileList);
		const listOfErrors = checkErrors(updatedParsedFileList);

		setCurrentWarnings(listOfWarnings);
		setCurrentErrors(listOfErrors);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentFileList, materialsMap]);

	useEffect(() => {
		if (kind !== ItemKinds.Material) return;

		if (currentFileList.length === 0) {
			setMaterialsMap([]);
			return;
		}

		// Auto-assign a default map to every file that doesn't have one yet.
		// First file → "diffuse"; any subsequent file → "thumbnail" (matches browser's
		// native select default, which shows the first enabled option).
		setMaterialsMap((prev) => {
			let updated = prev;
			currentFileList.forEach((file) => {
				if (!updated.some((item) => item.file.uid === file.uid)) {
					const defaultMap = updated.length === 0 ? "diffuse" : "thumbnail";
					updated = [...updated, { map: defaultMap, file }];
				}
			});
			return updated === prev ? prev : updated;
		});
	}, [currentFileList, kind]);

	useEffect(() => {
		setWarnings(currentWarnings);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentWarnings]);

	useEffect(() => {
		setErrors(currentErrors);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentErrors]);

	return (
		<StyledContainer divide={currentFileList.length > 0}>
			<div>
				<Typography as="h2" color="primary">
					{String(t("app.AntdDragger.modal.dragger.title"))}
				</Typography>
				<StyledDragger
					{...(({
						accept: acceptedExtensions[kind],
						multiple: true,
						customRequest: customRequest,
						showUploadList: false,
						fileList: currentFileList,
						beforeUpload: beforeUpload
					}) as any)}
				>
					<StyledDraggerContainer>
						<Icon name="FolderIcon" size="80px" color="grey700" />
						<Typography>
							{String(t("app.AntdDragger.modal.dragger.content"))}
						</Typography>
						{allowMultipleModels && (
							<Typography color="grey700">
								{String(t("app.AntdDragger.modal.dragger.multipleModels"))}
							</Typography>
						)}
						<StyledTagsContainer>
							{kind !== ItemKinds.Material && (
								<>
									{objectAccept.map((fileExtension) => (
										<StyledTags key={fileExtension}>
											{fileExtension === "obj"
												? "OBJ/MTL"
												: fileExtension.toUpperCase()}
										</StyledTags>
									))}
									{imageAccept.map((fileExtension) => (
										<StyledTags key={fileExtension}>
											{fileExtension.toUpperCase()}
										</StyledTags>
									))}
								</>
							)}

							{kind === ItemKinds.Material &&
								materialAcceptRef.map((fileExtension) => (
									<StyledTags key={fileExtension}>
										{fileExtension.toUpperCase()}
									</StyledTags>
								))}
						</StyledTagsContainer>
					</StyledDraggerContainer>
				</StyledDragger>
			</div>
			{(!!objectFiles.length ||
				!!materialFiles.length ||
				!!textureFiles.length) && (
				<StyledFilesCol>
					<StyledFileContainer>
						<Typography as="h2" color="primary">
							{String(t("app.AntdDragger.modal.dragger.uploadList"))}{" "}
						</Typography>
					</StyledFileContainer>
					<StyledFileContainer>
						<StyledWarningRow>
							<ul>
								{currentWarnings.map((warning) => (
									<li key={warning}>{String(t(warning))}</li>
								))}
							</ul>
							<ul>
								{currentErrors.map((error) => (
									<li key={error}>
										{String(t(error, {
											total: formatBytes(totalBytes, i18n.language),
											limit: formatBytes(MAX_UPLOAD_REQUEST_LABEL_BYTES, i18n.language),
										}))}
									</li>
								))}
							</ul>
						</StyledWarningRow>
						{/* {printWarning(parsedFileList)} */}
						{objectFiles.map((file) => renderLine(file))}
						{textureFiles.map((file) => renderLine(file))}
						{materialFiles.map((file) => renderLine(file))}
						{showTotal && (
							<>
								<StyledTotalRow over={totalTooLarge}>
									<Typography as="span">
										{String(t("app.AntdDragger.modal.dragger.total"))}
									</Typography>
									<span className="total-value">
										{formatBytes(totalBytes, i18n.language)} /{" "}
										{formatBytes(MAX_UPLOAD_REQUEST_LABEL_BYTES, i18n.language)}
									</span>
								</StyledTotalRow>
								<StyledTotalMeter over={totalTooLarge}>
									<i
										style={{
											width: `${Math.min(
												100,
												(totalBytes / MAX_UPLOAD_REQUEST_LABEL_BYTES) * 100,
											)}%`,
										}}
									/>
								</StyledTotalMeter>
							</>
						)}
					</StyledFileContainer>
				</StyledFilesCol>
			)}
		</StyledContainer>
	);
};

const StyledDragger = styled(Dragger)`
	span.ant-upload.ant-upload-btn {
		padding: 0;
	}
	div.ant-upload.ant-upload-drag {
		border: 1px dashed ${({ theme }) => theme.colors.grey400};
		background-color: ${({ theme }) => theme.colors.white};
		transition: border-color 0.2s ease;
		/* The height belongs here, not on the inner container. antd styles the
		   drop area as height:100% around a display:table button with another
		   height:100%, so a height set further in has to grow back up through
		   that chain — which left the drop area's own box shorter than its
		   contents and pushed the bottom edge under the modal footer. With a
		   definite height here, the table and the cell resolve against it and
		   nothing spills. */
		height: 30vh;
		box-sizing: border-box;
		overflow: hidden;
	}
	div.ant-upload.ant-upload-drag:hover,
	div.ant-upload.ant-upload-drag.ant-upload-drag-hover {
		border-color: ${({ theme }) => theme.colors.primary};
	}
`;

const StyledContainer = styled.div<{ divide: boolean }>`
	display: grid;
	grid-template-columns: ${({ divide }) => (divide ? "40% 60%" : "100% 0%")};
`;

const StyledTagsContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	max-width: 385px;
	margin: 10px;
`;

const StyledTags = styled.div`
	background-color: ${({ theme }) => theme.colors.grey300};
	color: ${({ theme }) => theme.colors.grey700};
	font-weight: ${({ theme }) => theme.typography.weight.normal};
	padding: 2px 5px;
	margin: 2px 5px;
	border-radius: 4px;
`;

const StyledDraggerContainer = styled(Container)`
	/* Fills the drop area, which is what defines the height now. */
	height: 100%;
	display: flex;
	flex-direction: column;
	/* No background of its own: the drop area behind it is already white, and an
	   opaque fill here covers the dashed border. */
	background-color: transparent;
`;

const StyledFilesCol = styled.div`
	flex: 1;
	width: 100%;
	padding: 0 0 0 20px;
`;

const StyledFileContainer = styled.div`
	margin-top: 8px;
`;

const StyledItemRow = styled.div<{ kind: string }>`
	display: grid;
	padding: 5px 10px;
	border-radius: 4px;
	width: 100%;
	grid-template-columns: ${({ kind }) =>
		kind === ItemKinds.Material
			? "1fr 1fr 64px 48px 56px"
			: "1fr 72px 52px 56px"};
	grid-gap: 8px;
	align-items: center;

	&:nth-child(even) {
		background: ${({ theme }) => theme.colors.grey200};
	}
	&:nth-child(odd) {
		background: ${({ theme }) => theme.colors.white};
	}
`;

const StyledSize = styled.span`
	text-align: right;
	font-variant-numeric: tabular-nums;
	color: ${({ theme }) => theme.colors.grey800};
	white-space: nowrap;
`;

const StyledTotalRow = styled.div<{ over: boolean }>`
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	margin-top: 8px;
	padding: 8px 10px 4px;
	border-top: 1px solid ${({ theme }) => theme.colors.grey400};
	font-weight: 500;

	.total-value {
		font-variant-numeric: tabular-nums;
		color: ${({ theme, over }) => (over ? theme.colors.red : "inherit")};
	}
`;

const StyledTotalMeter = styled.div<{ over: boolean }>`
	height: 4px;
	margin: 0 10px;
	border-radius: 2px;
	overflow: hidden;
	background: ${({ theme }) => theme.colors.grey200};

	i {
		display: block;
		height: 100%;
		background: ${({ theme, over }) =>
			over ? theme.colors.red : theme.colors.primary};
	}
`;

const StyledLineEnd = styled.div`
	display: flex;
	justify-content: space-between;
	height: 100%;
	align-items: center;
`;

const StyledWarningRow = styled.div`
	padding-bottom: 10px;
	border-bottom: 1px solid ${({ theme }) => theme.colors.grey200};
	ul {
		li {
			list-style-type: none;
		}
		&:first-child {
			li {
				color: ${({ theme }) => theme.colors.orange};
			}
		}
		&:last-child {
			li {
				color: ${({ theme }) => theme.colors.red};
			}
		}
	}
`;

export default HegiasDragger;
